import React, { useState, useMemo } from 'react';
import { useApiQuery, useApiMutation } from '../hooks/useApi';
import { apiClient } from '../utils/apiClient';
import './Templates.css';

interface GearItem {
  gearId: string;
  name: string;
  category: string;
  weight?: number;
  notes?: string;
}

interface TemplateGearItem {
  gearId: string;
  name: string;
  category: string;
}

interface Template {
  templateId: string;
  name: string;
  gearItems: TemplateGearItem[];
  createdAt: string;
  updatedAt: string;
}

interface TemplateFormData {
  name: string;
  gearItems: TemplateGearItem[];
}

const CATEGORIES = [
  { value: 'shelter', label: 'Shelter' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'cooking', label: 'Cooking' },
  { value: 'food', label: 'Food' },
  { value: 'water', label: 'Water' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'safety', label: 'Safety' },
  { value: 'first_aid', label: 'First Aid' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'tools', label: 'Tools' },
  { value: 'recreation', label: 'Recreation' },
  { value: 'other', label: 'Other' },
];

const Templates: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<Template | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    gearItems: [],
  });
  const [formErrors, setFormErrors] = useState<{ name?: string }>({});
  const [gearSearchTerm, setGearSearchTerm] = useState('');
  const [selectedGearCategory, setSelectedGearCategory] = useState('');

  // API hooks
  const { data: templates, loading, error, refetch } = useApiQuery<Template[]>(
    () => apiClient.getTemplates(),
    []
  );

  const { data: gearItems, loading: gearLoading } = useApiQuery<GearItem[]>(
    () => apiClient.getGear(),
    []
  );

  const { mutate: createTemplate, loading: creating } = useApiMutation<Template>();
  const { mutate: updateTemplate, loading: updating } = useApiMutation<Template>();
  const { mutate: deleteTemplate, loading: deleting } = useApiMutation<void>();
  const { mutate: duplicateTemplate, loading: duplicating } = useApiMutation<Template>();

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter(template =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [templates, searchTerm]);

  // Filter gear items for selection
  const filteredGearItems = useMemo(() => {
    if (!gearItems) return [];
    return gearItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(gearSearchTerm.toLowerCase());
      const matchesCategory = !selectedGearCategory || item.category === selectedGearCategory;
      const notAlreadySelected = !formData.gearItems.some(gi => gi.gearId === item.gearId);
      return matchesSearch && matchesCategory && notAlreadySelected;
    });
  }, [gearItems, gearSearchTerm, selectedGearCategory, formData.gearItems]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: { name?: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const templateData = {
      name: formData.name.trim(),
      gearItems: formData.gearItems,
    };

    let success = false;
    if (editingTemplate) {
      success = await updateTemplate(() => apiClient.updateTemplate(editingTemplate.templateId, templateData));
    } else {
      success = await createTemplate(() => apiClient.createTemplate(templateData));
    }

    if (success) {
      handleCloseForm();
      refetch();
    }
  };

  const handleDelete = async (templateId: string) => {
    const success = await deleteTemplate(() => apiClient.deleteTemplate(templateId));
    if (success) {
      setShowDeleteConfirm(null);
      refetch();
    }
  };

  const handleDuplicate = async (templateId: string) => {
    const success = await duplicateTemplate(() => apiClient.duplicateTemplate(templateId));
    if (success) {
      refetch();
    }
  };

  const handleOpenForm = (template?: Template) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ name: template.name, gearItems: [...template.gearItems] });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', gearItems: [] });
    }
    setFormErrors({});
    setGearSearchTerm('');
    setSelectedGearCategory('');
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({ name: '', gearItems: [] });
    setFormErrors({});
    setGearSearchTerm('');
    setSelectedGearCategory('');
  };

  const handleAddGear = (gear: GearItem) => {
    setFormData(prev => ({
      ...prev,
      gearItems: [...prev.gearItems, { gearId: gear.gearId, name: gear.name, category: gear.category }],
    }));
  };

  const handleRemoveGear = (gearId: string) => {
    setFormData(prev => ({
      ...prev,
      gearItems: prev.gearItems.filter(item => item.gearId !== gearId),
    }));
  };

  const getCategoryLabel = (categoryValue: string) =>
    CATEGORIES.find(c => c.value === categoryValue)?.label || categoryValue;

  if (loading) {
    return (
      <div className="templates-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="templates-page">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to load templates</h3>
          <p>{error}</p>
          <button className="primary-button" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-page">
      <div className="page-header">
        <div>
          <h1>Trip Templates</h1>
          <p>Create reusable packing lists for different trip types</p>
        </div>
        <button className="primary-button" onClick={() => handleOpenForm()}>
          Create New Template
        </button>
      </div>

      <div className="templates-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search templates..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="templates-grid">
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            {templates?.length === 0 ? (
              <>
                <h3>No templates yet</h3>
                <p>Create your first template to start building reusable packing lists for your adventures.</p>
                <button className="primary-button" onClick={() => handleOpenForm()}>
                  Create Your First Template
                </button>
              </>
            ) : (
              <>
                <h3>No matching templates found</h3>
                <p>Try adjusting your search.</p>
                <button className="secondary-button" onClick={() => setSearchTerm('')}>
                  Clear Search
                </button>
              </>
            )}
          </div>
        ) : (
          filteredTemplates.map(template => (
            <div key={template.templateId} className="template-card">
              <div className="template-card-header">
                <h3 className="template-name">{template.name}</h3>
                <div className="template-actions">
                  <button
                    className="action-button preview-button"
                    onClick={() => setShowPreview(template)}
                    title="Preview template"
                  >
                    👁️
                  </button>
                  <button
                    className="action-button duplicate-button"
                    onClick={() => handleDuplicate(template.templateId)}
                    title="Duplicate template"
                    disabled={duplicating}
                  >
                    📋
                  </button>
                  <button
                    className="action-button edit-button"
                    onClick={() => handleOpenForm(template)}
                    title="Edit template"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => setShowDeleteConfirm(template.templateId)}
                    title="Delete template"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="template-details">
                <div className="template-gear-count">
                  <span className="detail-label">Gear Items:</span>
                  <span className="detail-value">{template.gearItems.length} items</span>
                </div>

                {template.gearItems.length > 0 && (
                  <div className="template-gear-preview">
                    <span className="detail-label">Categories:</span>
                    <div className="category-tags">
                      {Array.from(new Set(template.gearItems.map(item => item.category)))
                        .slice(0, 3)
                        .map(category => (
                          <span key={category} className={`category-tag category-${category}`}>
                            {getCategoryLabel(category)}
                          </span>
                        ))}
                      {Array.from(new Set(template.gearItems.map(item => item.category))).length > 3 && (
                        <span className="category-tag category-more">
                          +{Array.from(new Set(template.gearItems.map(item => item.category))).length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Template Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content template-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
              <button className="close-button" onClick={handleCloseForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="template-form">
              <div className="form-section">
                <div className="form-group">
                  <label htmlFor="name">Template Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, name: e.target.value }));
                      if (formErrors.name) setFormErrors({});
                    }}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="e.g. Moto Camping, Weekend Backpacking"
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>
              </div>

              <div className="form-section">
                <h3>Selected Gear ({formData.gearItems.length} items)</h3>

                {formData.gearItems.length === 0 ? (
                  <div className="no-gear-selected">
                    <p>No gear items selected yet. Add items from your inventory below.</p>
                  </div>
                ) : (
                  <div className="selected-gear-list">
                    {formData.gearItems.map(item => (
                      <div key={item.gearId} className="selected-gear-item">
                        <div className="gear-info">
                          <span className="gear-name">{item.name}</span>
                          <span className={`category-badge category-${item.category}`}>
                            {getCategoryLabel(item.category)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="remove-gear-button"
                          onClick={() => handleRemoveGear(item.gearId)}
                          title="Remove from template"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3>Add Gear from Inventory</h3>

                {gearLoading ? (
                  <div className="gear-loading">
                    <div className="loading-spinner small"></div>
                    <span>Loading gear inventory...</span>
                  </div>
                ) : !gearItems || gearItems.length === 0 ? (
                  <div className="no-gear-available">
                    <p>No gear items in your inventory. Add gear items first to include them in templates.</p>
                  </div>
                ) : (
                  <>
                    <div className="gear-filters">
                      <div className="search-box">
                        <input
                          type="text"
                          placeholder="Search gear..."
                          className="search-input small"
                          value={gearSearchTerm}
                          onChange={(e) => setGearSearchTerm(e.target.value)}
                        />
                      </div>
                      <select
                        className="filter-select small"
                        value={selectedGearCategory}
                        onChange={(e) => setSelectedGearCategory(e.target.value)}
                      >
                        <option value="">All Categories</option>
                        {CATEGORIES.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="available-gear-list">
                      {filteredGearItems.length === 0 ? (
                        <div className="no-gear-found">
                          <p>No available gear items match your search criteria.</p>
                        </div>
                      ) : (
                        filteredGearItems.map(gear => (
                          <div key={gear.gearId} className="available-gear-item">
                            <div className="gear-info">
                              <span className="gear-name">{gear.name}</span>
                              <span className={`category-badge category-${gear.category}`}>
                                {getCategoryLabel(gear.category)}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="add-gear-button"
                              onClick={() => handleAddGear(gear)}
                              title="Add to template"
                            >
                              +
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={handleCloseForm}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={creating || updating}
                >
                  {creating || updating ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(null)}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showPreview.name}</h2>
              <button className="close-button" onClick={() => setShowPreview(null)}>×</button>
            </div>

            <div className="preview-content">
              <div className="preview-stats">
                <div className="stat">
                  <span className="stat-label">Total Items:</span>
                  <span className="stat-value">{showPreview.gearItems.length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Categories:</span>
                  <span className="stat-value">
                    {Array.from(new Set(showPreview.gearItems.map(item => item.category))).length}
                  </span>
                </div>
              </div>

              <div className="preview-gear-list">
                <h4>Gear Items</h4>
                {showPreview.gearItems.length === 0 ? (
                  <p className="no-items">No gear items in this template.</p>
                ) : (
                  <div className="gear-items-by-category">
                    {Array.from(new Set(showPreview.gearItems.map(item => item.category)))
                      .sort()
                      .map(category => (
                        <div key={category} className="category-section">
                          <h5 className="category-header">
                            <span className={`category-badge category-${category}`}>
                              {getCategoryLabel(category)}
                            </span>
                            <span className="category-count">
                              ({showPreview.gearItems.filter(item => item.category === category).length} items)
                            </span>
                          </h5>
                          <ul className="category-items">
                            {showPreview.gearItems
                              .filter(item => item.category === category)
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(item => (
                                <li key={item.gearId} className="gear-item">
                                  {item.name}
                                </li>
                              ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="preview-actions">
              <button className="secondary-button" onClick={() => setShowPreview(null)}>
                Close
              </button>
              <button
                className="primary-button"
                onClick={() => { setShowPreview(null); handleOpenForm(showPreview); }}
              >
                Edit Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Template</h2>
              <button className="close-button" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>

            <div className="delete-content">
              <p>Are you sure you want to delete this template? This action cannot be undone.</p>
              <p className="template-name-confirm">
                <strong>{templates?.find(t => t.templateId === showDeleteConfirm)?.name}</strong>
              </p>
            </div>

            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
